import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import { Link } from "react-router";
import React from "react";
import { DelayRendering, SmallLoadingSpinner } from "../../../routes/modal-loading-spinner";
import { LoadingState } from "../../../domain/data-layer/loading-state";
import { headlineStyles, overflowStylesGreen, wrapperStyles } from "./GuidedSellingStep";
import { FinalGuidedSellingStep, SwarovskiOptikLocaleFrontend, type GuidedSellingTranslation } from "@swo/storefront";
import { SwarovskiButton } from "../../_shared-components/ui-elements/SwarovskiButton";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { CSS_MQ } from "../../../styles/breakpoints";
import { StepNavigationBarRestartButton } from "./StepNavigationBarRestartButton";
import { GuidedSellingResultDto } from "@swo/storefront";
import { showPrice } from "@swo/shared";
import { getCleanProductComparisonPageLink } from "../../../layouts/product-comparison-page-layout/elements/state";
import { ContentfulImage2 } from "../../_shared-components/image-related/ContentfulImage2";
import { formatOptionalStartingPrice } from "../../../domain/swarovski-optik/products/price/format-price";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import cn from "classnames";
import { ANIMATED } from "../../../styles/utils/animated";
import { useGeneralTranslationsContext } from "../../../global/hooks/use-general-translations-context";
import { guidedSellingTrackingAspect } from "../../../domain/google-tag-manager/guided-selling-tracking-aspect";
import { useKeyedMounted } from "../../../hooks/use-keyed-mounted";
import { useMounted } from "../../../hooks/use-mounted";
import { LeftArrowSvg, RightArrowSvg } from "../../../assets";
import { T } from "../../T";

export const GuidedSellingFinalStepScreen: ReactTFC<{
  currentStep: FinalGuidedSellingStep;
  result: LoadingState<{
    selected: GuidedSellingResultDto | undefined;
    selectedIndex: number;
    resultsCount: number;
  }>;
  onRestart: () => void;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
  translations: GuidedSellingTranslation;
  onIndexRequested: (idx: number) => void;
}> = props => {
  const ErrorPage = (
    <div css={wrapperStyles}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <p
          css={css`
            color: black;

            ${headlineStyles};

            margin-bottom: 0.75em;
            ${CSS_MQ.atLeastMd} {
              margin-bottom: 1.5em;
            }
          `}
        >
          {props.translations.noResultsFound.value}
        </p>

        <StepNavigationBarRestartButton
          onClickRestart={props.onRestart}
          isDisabled={false}
          translations={props.translations}
        />
      </div>
    </div>
  );

  if (props.result.__tag === "loading") {
    return (
      <div css={wrapperStyles} className={"loading"}>
        <div
          css={css`
            display: flex;
            justify-content: center;
          `}
        >
          <DelayRendering delayMs={300}>
            <SmallLoadingSpinner size={"2x"} color={"green"} />
          </DelayRendering>
        </div>
      </div>
    );
  } else if (props.result.__tag === "error") {
    return ErrorPage;
  } else {
    return props.result.data.selected ? (
      <GuidedSellingFinalStepResults
        result={props.result.data}
        currentStep={props.currentStep}
        onRestart={props.onRestart}
        localeForNumberFormat={props.localeForNumberFormat}
        translations={props.translations}
        onIndexRequested={props.onIndexRequested}
      />
    ) : (
      ErrorPage
    );
  }
};

const animated = {
  initial: ANIMATED.zero,
  first: ANIMATED.veryFast,
  second: ANIMATED.fast,
  third: ANIMATED.medium,
  fourth: ANIMATED.slow,
  last: ANIMATED.verySlow,
};

const GuidedSellingFinalStepResults: ReactTFC<{
  result: {
    selected: GuidedSellingResultDto | undefined;
    selectedIndex: number;
    resultsCount: number;
  };
  currentStep: FinalGuidedSellingStep;
  onRestart: () => void;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
  translations: GuidedSellingTranslation;
  onIndexRequested: (idx: number) => void;
}> = props => {
  const fromPriceLabel = useGeneralTranslationsContext()("fromPriceLabel");
  const lowestPriceLast30DaysTranslation = useGeneralTranslationsContext()("lowestPriceLast30Days");

  const currentIndex = props.result.selectedIndex;
  const count = props.result.resultsCount;
  const { selected } = props.result;

  const priceLabel =
    selected &&
    selected.price &&
    (selected.price.tag === "starting-price"
      ? formatOptionalStartingPrice(
          {
            startingPrice: selected.price.value,
            discountedStartingPrice: selected.discountedPrice?.value,
            lowestPrice30Days: selected.lowestPrice30Days,
            discountPercentage: selected.discountPercentage,
          },
          fromPriceLabel,
          lowestPriceLast30DaysTranslation,
          props.localeForNumberFormat,
        )
      : showPrice(selected.price.value, props.localeForNumberFormat));

  const shouldAnimationBeTriggered = useKeyedMounted(props.result.selected || "", 50);
  const isMounted = useMounted();

  const iconStyles = css`
    width: var(--i);
    height: var(--i);
  `;

  const buttonStyles = css`
    display: flex;
    justify-content: center;
    align-items: center;

    background: #f5f5f5;
    border-radius: 50%;

    &[disabled] {
      cursor: not-allowed;

      svg {
        color: ${CSS_COLORS.Grey};
      }
    }

    svg {
      transition: 0.2s ease-out transform;
      padding: var(--p);
    }

    &.previous {
      svg {
        padding-right: calc(var(--p) + 3px);
      }
    }
    &.next {
      svg {
        padding-left: calc(var(--p) + 3px);
      }
    }

    &.previous:not([disabled]):hover {
      svg {
        transform: translateX(-2px);
      }
    }

    &.next:not([disabled]):hover {
      svg {
        transform: translateX(2px);
      }
    }
  `;

  return (
    <>
      <div
        css={css`
          z-index: 4 !important;
          position: absolute;

          pointer-events: none;
          > * {
            pointer-events: all;
          }

          --i: 36px;
          --p: 2px;
          height: 36px;
          grid-row: 4;
          bottom: -36px;

          ${CSS_MQ.atLeastSm} {
            --i: 48px;
            --p: 4px;
            height: calc(100% - var(--i) - 2 * 46px);
            grid-row: 3;
            bottom: auto;
          }

          ${CSS_MQ.atLeastLg} {
            --i: 64px;
            --p: 8px;
          }

          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <button
          disabled={currentIndex === 0}
          onClick={() => props.onIndexRequested(currentIndex - 1)}
          className={"previous"}
          css={buttonStyles}
        >
          <LeftArrowSvg css={iconStyles} />
        </button>
        <button
          disabled={currentIndex === count - 1}
          onClick={() => props.onIndexRequested(currentIndex + 1)}
          className={"next"}
          css={buttonStyles}
        >
          <RightArrowSvg css={iconStyles} />
        </button>
      </div>

      <div
        className={cn({ triggered: shouldAnimationBeTriggered })}
        css={[
          animated.last,
          css`
            display: flex;
            justify-content: space-between;
            height: var(--bar-height);

            ${CSS_MQ.atLeastMd} {
              align-self: center;
              width: 100%;
              max-width: var(--size);
            }

            justify-self: flex-start !important;
          `,
        ]}
      >
        <StepNavigationBarRestartButton
          onClickRestart={props.onRestart}
          isDisabled={false}
          translations={props.translations}
        />
      </div>

      {props.result && (
        <span
          className={cn({ triggered: isMounted })}
          css={[
            animated.initial,
            css`
              ${headlineStyles};

              margin-bottom: 0.75em;
              ${CSS_MQ.atLeastMd} {
                margin-bottom: 1.5em;
              }
            `,
          ]}
        >
          <span className={"u-uppercase"}>{props.currentStep.headline}</span>
        </span>
      )}

      <div css={wrapperStyles}>
        <div
          css={[
            overflowStylesGreen,
            css`
              flex-grow: 1;
            `,
          ]}
        >
          {typeof props.result === "undefined" && (
            <div>
              <p
                css={[
                  fontSize.x2,
                  css`
                    text-align: center;
                    font-weight: 300;
                  `,
                ]}
              >
                No results found.
              </p>
            </div>
          )}
          {selected && (
            <>
              <div
                key={selected.id}
                css={[animated.first, resultWrapperStyle]}
                className={cn({ triggered: shouldAnimationBeTriggered })}
              >
                <Link to={selected.path} className={"_image"}>
                  <ContentfulImage2
                    assetId={selected.image?.sys.id}
                    contentfulUrl={selected.image?.url}
                    withTransparency={true}
                    alt={""}
                    css={css`
                      width: 100%;
                      display: block;
                      height: 100%;
                      object-fit: contain;
                      object-position: top;
                    `}
                    sizes="(max-width: 1200px) 100vw, 500px"
                    cropping={undefined}
                  />
                </Link>

                <div className={"_price"}>
                  <span
                    css={css`
                      display: block;
                      color: ${CSS_COLORS.Green};

                      ${FONTS.SwarovskiFutura300};

                      font-size: 24px;
                      ${CSS_MQ.atLeastMd} {
                        font-size: 30px;
                      }
                      letter-spacing: 1px;
                      line-height: 1.2;
                    `}
                  >
                    {selected.name}
                  </span>

                  <span
                    className={cn({ triggered: shouldAnimationBeTriggered })}
                    css={[
                      animated.second,
                      css`
                        ${FONTS.SwarovskiFutura500};
                        ${fontColor.black};

                        font-size: 16px;
                        letter-spacing: 1px;
                      `,
                    ]}
                  >
                    {priceLabel ? <T>{priceLabel}</T> : null}
                  </span>
                </div>

                <div
                  className={cn("_description", { triggered: shouldAnimationBeTriggered })}
                  css={[
                    animated.third,
                    css`
                      flex-grow: 1;
                      flex-shrink: 1;

                      position: relative;

                      --h: 50px;
                      margin-bottom: 20px;

                      &:before {
                        content: "";
                        position: absolute;
                        left: 0;
                        bottom: 0;
                        width: calc(100% - 3px); //scrollbar
                        height: var(--h);
                        z-index: 1;
                        background: linear-gradient(to bottom, ${CSS_COLORS.GreenWhiteA50}, ${CSS_COLORS.GreenWhite});

                        pointer-events: none;
                      }
                    `,
                  ]}
                >
                  {selected.description && (
                    <div
                      css={[
                        FONTS.SwarovskiFutura300,
                        fontSize.x2,
                        fontColor.black,
                        css`
                          position: absolute;

                          margin: 0;

                          width: 100%;
                          height: 100%;
                          top: 0;
                          left: 0;

                          overflow-y: scroll;

                          &::-webkit-scrollbar {
                            width: 3px;
                          }

                          &::-webkit-scrollbar-track {
                            background: transparent;
                          }

                          &::-webkit-scrollbar-thumb {
                            background: ${CSS_COLORS.Green};
                          }

                          .last-rt-node {
                            padding-bottom: calc(var(--h) - 20px);
                            margin-bottom: 0;
                          }
                        `,
                      ]}
                    >
                      <p>{selected.description}</p>
                    </div>
                  )}
                </div>

                <div
                  className={cn("_buttons", { triggered: shouldAnimationBeTriggered })}
                  css={[
                    animated.fourth,
                    css`
                      display: grid;
                      grid-gap: 5px;

                      // Some space is needed to be able to click secondary button
                      // and left/right navigation (especially on mobiles)
                      margin-bottom: 12px;
                    `,
                  ]}
                >
                  <SwarovskiButton
                    variant={"green"}
                    as={"a"}
                    isInternal={false}
                    target={"_blank"}
                    scrollToTop={true}
                    link={selected.path}
                    css={css`
                      text-align: center;
                    `}
                    onClick={() => guidedSellingTrackingAspect.guidedSellingResultPdp(selected.productKey).push()}
                  >
                    <span
                      css={css`
                        display: flex;
                        justify-content: center;
                      `}
                    >
                      {props.translations.detailsPage.value}
                    </span>
                  </SwarovskiButton>

                  <SwarovskiButton
                    variant={"ghost-underline"}
                    as={"a"}
                    isInternal={false}
                    target={"_blank"}
                    scrollToTop={true}
                    disabled={!selected.pcpLink}
                    link={
                      selected.pcpLink ? getCleanProductComparisonPageLink(selected.pcpLink, selected.productKey) : ""
                    }
                    onClick={() =>
                      guidedSellingTrackingAspect.guidedSellingResultComparison(selected.productKey).push()
                    }
                  >
                    <span
                      css={css`
                        display: flex;
                        justify-content: center;
                      `}
                    >
                      {props.translations.compareProductsPage.value}
                    </span>
                  </SwarovskiButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const resultWrapperStyle = css`
  display: grid;

  @media only screen and (max-height: 600px) {
    grid-template-rows: 80px auto 1fr auto;
    grid-template-areas:
      "image"
      "price"
      "description"
      "buttons";
  }

  grid-template-rows: 120px auto 1fr auto;
  grid-template-areas:
    "image"
    "price"
    "description"
    "buttons";

  grid-row-gap: 6px;
  ${CSS_MQ.atLeastMd} {
    grid-template-rows: 200px auto 1fr auto;
    grid-template-areas:
      "image"
      "price"
      "description"
      "buttons";
    grid-row-gap: 10px;
  }

  ${CSS_MQ.atLeastLg} {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "image price"
      "image description"
      "image buttons";

    grid-column-gap: 30px;
    grid-row-gap: 20px;
  }

  > ._image {
    grid-area: image;
    align-self: flex-start;

    width: 100%;
    height: 100%;
  }

  > ._price {
    grid-area: price;

    display: flex;
    flex-direction: column;

    ${CSS_MQ.atMostSm} {
      align-items: center;
    }
  }

  > ._description {
    grid-area: description;
  }

  > ._buttons {
    grid-area: buttons;
  }

  grid-auto-flow: row;

  height: 100%;
  //padding-bottom: 20px;
`;
